/**
 * RasterShape - Raster images (PNG, JPG, BMP) rendered as Konva.Image
 * Used for bitmap/raster engraving operations in the template editor
 */
import { useEffect, useRef, useState } from 'react';
import { Image, Group } from 'react-konva';
import Konva from 'konva';
import type { TemplateShape } from '@jigflow/shared';
import { CursorMode } from '../useTemplateStore';

interface RasterShapeProps {
    shape: TemplateShape;
    isSelected: boolean;
    workAreaWidth: number;
    workAreaHeight: number;
    scale: number;
    cursorMode?: CursorMode;
    onSelect: () => void;
    onChange: (updates: Partial<TemplateShape>) => void;
    onDragMove?: (id: string, x: number, y: number, width: number, height: number, node: Konva.Node) => void;
    onDragEnd?: () => void;
    registerRef: (id: string, node: Konva.Node | null) => void;
}

export function RasterShape({
    shape,
    isSelected,
    workAreaWidth: _workAreaWidth,
    workAreaHeight: _workAreaHeight,
    scale: _scale,
    cursorMode = 'move',
    onSelect,
    onChange,
    onDragMove,
    onDragEnd: onDragEndCallback,
    registerRef
}: RasterShapeProps) {
    void _scale;
    void _workAreaWidth;
    void _workAreaHeight;

    const canDrag = cursorMode === 'move' && isSelected;
    const groupRef = useRef<Konva.Group>(null);
    const imageRef = useRef<Konva.Image>(null);
    const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);

    useEffect(() => {
        registerRef(shape.id, groupRef.current);
        return () => registerRef(shape.id, null);
    }, [shape.id, registerRef]);

    useEffect(() => {
        if (!shape.rasterData) return;

        const img = new window.Image();
        img.onload = () => {
            setLoadedImage(img);
        };
        img.onerror = (err) => {
            console.error('[RasterShape] Failed to load raster image:', err);
        };
        img.src = shape.rasterData;

        return () => {
            setLoadedImage(null);
        };
    }, [shape.rasterData]);

    const handleDragMoveInternal = (e: Konva.KonvaEventObject<DragEvent>) => {
        const node = e.target;
        if (onDragMove) {
            onDragMove(shape.id, node.x(), node.y(), shape.width, shape.height, node);
        }
    };

    const handleDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
        const node = e.target;
        onChange({
            x: node.x(),
            y: node.y()
        });
        if (onDragEndCallback) {
            onDragEndCallback();
        }
    };

    const handleTransformEnd = (e: Konva.KonvaEventObject<Event>) => {
        const node = e.target as Konva.Group;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();

        const newWidth = Math.max(5, shape.width * scaleX);
        const newHeight = Math.max(5, shape.height * scaleY);

        node.scaleX(1);
        node.scaleY(1);

        onChange({
            x: node.x(),
            y: node.y(),
            width: newWidth,
            height: newHeight,
            rotation: node.rotation()
        });
    };

    if (!shape.rasterData) return null;

    const allOpsOff = shape.engrave === false;
    const opacity = allOpsOff ? 0.3 : 1;

    return (
        <Group
            ref={groupRef}
            x={shape.x}
            y={shape.y}
            rotation={shape.rotation || 0}
            draggable={canDrag}
            onClick={onSelect}
            onTap={onSelect}
            onDragMove={canDrag ? handleDragMoveInternal : undefined}
            onDragEnd={canDrag ? handleDragEnd : undefined}
            onTransformEnd={canDrag ? handleTransformEnd : undefined}
        >
            <Image
                ref={imageRef}
                image={loadedImage || undefined}
                x={0}
                y={0}
                width={shape.width}
                height={shape.height}
                opacity={opacity}
            />
        </Group>
    );
}
